// OL-6 outputReviewer Momus (omo B-5 영감, D6=A 도입 결정).
//
// 응답 finish 직전 risk-marker gated LLM-as-judge hop. OKAY default bias.
// env-var USE_OUTPUT_REVIEWER off default — A/B 측정 후 production gating 결정.
//
// 효과: P2-G Single-answer-missed 60-70% (L11-COMPUTE-TEXT-001 등) + raw key/scalar 30% 보조.
//
// 비용 추정: gpt-4o-mini ~$0.0001/gated turn + latency +1-2s per gated turn.
// 위험: false REJECT (OKAY default bias로 완화) + fail-open (parse 실패 → OKAY).
//
// 의존성: FL-2 (finish handler가 reviewOutput 결과로 retry 분기)

export const OUTPUT_REVIEWER_PROMPT = `You are Momus, a strict but fair output reviewer for an AI assistant managing CrossFit gym members.

The assistant has produced a response. Evaluate if it correctly answers the user's question given the available data.

DEFAULT BIAS — OKAY:
- If the response is reasonable and uses available data, return OKAY.
- Only return REJECT if there is a clear, specific problem that would mislead the user.

REJECT criteria (any one is sufficient):
1. The response mentions a specific number, but the buffer/summary doesn't support it.
2. The response says "data not available" when relevant data IS in the buffer.
3. The response answers a different question than asked (e.g., user asked "how many?" but response shows a list with no count).
4. The response contains raw camelCase keys (e.g., totalMembers, churnRate) instead of Korean labels.

Output format (JSON):
{"verdict": "OKAY" | "REJECT", "reason": "<one short sentence if REJECT, else empty>", "suggestedRetry": "<one short retry hint if REJECT, else empty>"}

If you cannot decide confidently → return OKAY.`;

const RISK_MARKER_PATTERNS = Object.freeze([
  // 숫자 (정수 / 천단위 / 소수 / %)
  /\d+(?:,\d{3})*(?:\.\d+)?%?/,
  // 숫자 + 단위 (Korean) — '명'/'회'/'개'/'건' 단독 매칭은 false positive (회원/확인 등) 차단
  /\d+\s*(?:명|회|개|건|만원|원|시간|분)/,
  // 통계/비교 keyword (단어 자체로 의미)
  /평균|합계|증가|감소|상승|하락|대비/,
]);

/**
 * 응답 텍스트가 review 가치 있는지 — risk marker 있어야 LLM-as-judge hop.
 *
 * @param {string} text — finish text 또는 outro
 * @returns {boolean}
 */
export function shouldReview(text /* , buffer 자리 (현재 사용 X, 미래 확장) */) {
  if (typeof text !== 'string') return false;
  if (text.trim().length === 0) return false;
  for (const re of RISK_MARKER_PATTERNS) {
    if (re.test(text)) return true;
  }
  return false;
}

/**
 * LLM-as-judge hop. 다층 skip 보호 — env off / no risk marker / llmCall missing / throw / parse 실패 → OKAY.
 *
 * @param {object} params
 * @param {string} params.finalText
 * @param {object} [params.buffer]
 * @param {string} [params.userInput]
 * @param {Function} [params.llmCall] — async (messages) => string|object
 * @returns {Promise<{verdict: 'OKAY'|'REJECT', reason: string, suggestedRetry: string, skipped?: string}>}
 */
export async function reviewOutput({ finalText, buffer, userInput, llmCall }) {
  if (!_isEnabled()) {
    return { verdict: 'OKAY', reason: '', suggestedRetry: '', skipped: 'env-disabled' };
  }
  if (!shouldReview(finalText, buffer)) {
    return { verdict: 'OKAY', reason: '', suggestedRetry: '', skipped: 'no-risk-marker' };
  }
  if (typeof llmCall !== 'function') {
    return { verdict: 'OKAY', reason: '', suggestedRetry: '', skipped: 'no-llmCall' };
  }

  const messages = [
    { role: 'system', content: OUTPUT_REVIEWER_PROMPT },
    {
      role: 'user',
      content: JSON.stringify({
        userInput: userInput ?? '',
        finalText: finalText ?? '',
        buffer: _summarizeBuffer(buffer),
      }),
    },
  ];

  let raw;
  try {
    raw = await llmCall(messages);
  } catch {
    return { verdict: 'OKAY', reason: '', suggestedRetry: '', skipped: 'llmCall-error' };
  }

  return _parseReviewerResponse(raw);
}

// ────────────────────────────────────────────────
// internal
// ────────────────────────────────────────────────

function _isEnabled() {
  if (typeof process === 'undefined' || !process.env) return false;
  const v = process.env.USE_OUTPUT_REVIEWER;
  if (!v) return false;
  return /^(1|true|on|yes)$/i.test(v);
}

function _summarizeBuffer(buffer) {
  if (!buffer || typeof buffer !== 'object') return null;
  return {
    itemCount: Array.isArray(buffer.items) ? buffer.items.length : 0,
    summaryKeys: buffer.summary && typeof buffer.summary === 'object'
      ? Object.keys(buffer.summary).slice(0, 20)
      : [],
    columnLabels: Array.isArray(buffer.resolvedColumnLabels)
      ? buffer.resolvedColumnLabels.slice(0, 20)
      : [],
  };
}

function _parseReviewerResponse(raw) {
  let text = '';
  if (typeof raw === 'string') {
    text = raw;
  } else if (raw && typeof raw === 'object') {
    text = raw.content ?? raw.message ?? '';
    if (!text && typeof raw.toString === 'function' && raw.toString() !== '[object Object]') {
      text = raw.toString();
    }
  }
  if (!text) return { verdict: 'OKAY', reason: '', suggestedRetry: '', skipped: 'empty-response' };

  try {
    const m = text.match(/\{[\s\S]*\}/);
    const jsonStr = m ? m[0] : text;
    const parsed = JSON.parse(jsonStr);
    const verdict = parsed.verdict === 'REJECT' ? 'REJECT' : 'OKAY';
    return {
      verdict,
      reason: typeof parsed.reason === 'string' ? parsed.reason : '',
      suggestedRetry: typeof parsed.suggestedRetry === 'string' ? parsed.suggestedRetry : '',
    };
  } catch {
    return { verdict: 'OKAY', reason: '', suggestedRetry: '', skipped: 'parse-error' };
  }
}

export const _isEnabledForTest = _isEnabled;
export const _summarizeBufferForTest = _summarizeBuffer;
export const _parseReviewerResponseForTest = _parseReviewerResponse;
export const _RISK_MARKER_PATTERNS = RISK_MARKER_PATTERNS;
